/**
 * LA RELECTURE DU GESTE ET LA CONSTRUCTION REJOUÉE (atelier de figures, lot L1,
 * F4 ; `docs/pilotage/atelier-figures/CONCEPTION.md` § 3 ;
 * `SPEC-REPRESENTATION-VISUELLE.md` § 7.7).
 *
 * Deux fonctions PURES, déterministes, jamais un modèle :
 *
 *  - `decrireGesteDeTrace` : ce que l'élève a tracé, dit comme on le lirait à
 *    voix haute, AVANT toute correction — « On a lu : une droite qui passe
 *    par (3 ; 2) et (2 ; 3). » Seuls les objets FINAUX sont dits : les deux
 *    points qui portent une droite sont dans sa phrase, pas à côté. Un trait
 *    de construction masqué, une mesure, un texte ne sont pas « la réponse ».
 *  - `etapesDeLaConstruction` : la construction de référence du prof, une
 *    phrase par objet, dans l'ordre — ce que « Voir la construction » rejoue
 *    pas à pas sur la figure.
 *
 * ─── Pourquoi ici, et pas dans l'application ────────────────────────────────
 *
 * La relecture se fait sur l'appareil (« Non, je recommence » ne laisse aucune
 * trace : rien ne part au serveur). Mais la phrase lue doit être la MÊME
 * partout où elle se relira : la console du tuteur (« la relecture confirmée
 * par l'élève », CONCEPTION § 4) et le serveur, s'il garde un jour la phrase
 * confirmée avec l'essai. Elle vit donc à côté de `decrireConstruction`.
 *
 * Le nom : `decrireGeste` existe déjà (`decrire.ts`), pour le geste V1
 * (`GesteEleveDto`, un point ou une poignée). Celle-ci décrit un DOCUMENT de
 * construction de l'atelier.
 */
import { estIdEnonce, referencesDe, resoudreConstruction, } from './construction.js';
import { decrireConstruction } from './decrire-v2.js';
import { decrireCaracteristiques, reduireEsquisse } from './esquisse.js';
import { formaterNombre } from './geometrie.js';
/** Un nombre lu : virgule décimale, signe moins typographique (« −1 », « 0,5 »). */
function nombre(v) {
    return formaterNombre(v).replace(/^-/u, '−');
}
/** Un point lu : « (3 ; 2) », d'un seul tenant — insécable, il ne se coupe pas en fin de ligne. */
export function coordonneesLues(p) {
    return `(${nombre(p.x)}\u00a0;\u00a0${nombre(p.y)})`;
}
/** Ce qui ne se lit pas comme une réponse : un trait masqué, une mesure, une étiquette, une transformation. */
const HORS_REPONSE = new Set([
    'mesure_longueur',
    'mesure_angle',
    'texte',
    'table_valeurs',
    'translation',
    'rotation',
    'reflexion',
    'homothetie',
]);
/** Le nom d'un polygone par son nombre de sommets. */
function nomPolygone(n) {
    if (n === 3)
        return 'triangle';
    if (n === 4)
        return 'quadrilatère';
    return `polygone à ${n} sommets`;
}
/** Une liste lue : « a, b et c ». */
function enListe(morceaux) {
    if (morceaux.length <= 1)
        return morceaux[0] ?? '';
    return `${morceaux.slice(0, -1).join(', ')} et ${morceaux[morceaux.length - 1] ?? ''}`;
}
/**
 * Le lecteur d'un document résolu : la phrase d'un objet, sans majuscule ni
 * point final (« une droite qui passe par (0 ; −1) et (1 ; 3) »).
 */
function lecteur(doc, resolue) {
    /* Le repli : la phrase de `decrireConstruction`, la même que la liste des objets. */
    const repli = new Map(decrireConstruction(doc, resolue).phrases.map((p) => [p.id, p.texte]));
    const resolu = (id) => resolue.objets.get(id);
    const point = (id) => {
        const o = resolu(id);
        return o?.genre === 'point' ? o.p : null;
    };
    /** Un objet cité : un point de l'énoncé par son nom, un point de l'élève par ses coordonnées. */
    const cite = (id) => {
        const o = resolu(id);
        if (o?.genre === 'point') {
            return estIdEnonce(id) && o.nom !== undefined ? o.nom : coordonneesLues(o.p);
        }
        if (estIdEnonce(id)) {
            if (o?.genre === 'ligne')
                return 'la droite donnée';
            if (o?.genre === 'cercle')
                return 'le cercle donné';
            return 'la figure de départ';
        }
        return o?.nom ?? 'l’objet tracé';
    };
    const parLaTransformation = (id) => {
        const def = doc.objets.find((o) => o.id === id)?.def;
        switch (def?.type) {
            case 'translation':
                return `par la translation qui mène ${cite(def.de)} en ${cite(def.a)}`;
            case 'rotation': {
                const sens = def.angle >= 0 ? 'antihoraire' : 'horaire';
                return `par la rotation de centre ${cite(def.centre)}, de ${nombre(Math.abs(def.angle))}° dans le sens ${sens}`;
            }
            case 'reflexion':
                return `par la réflexion d’axe ${cite(def.axe)}`;
            case 'homothetie':
                return `par l’homothétie de centre ${cite(def.centre)} et de rapport ${nombre(def.rapport)}`;
            default:
                return '';
        }
    };
    /** Ce que donne un objet résolu, quelle que soit sa définition. */
    const forme = (o) => {
        switch (o?.genre) {
            case 'point':
                return `un point en ${coordonneesLues(o.p)}`;
            case 'ligne':
                if (o.sorte === 'segment') {
                    return `un segment qui relie ${coordonneesLues(o.a)} et ${coordonneesLues(o.b)}`;
                }
                if (o.sorte === 'demi_droite') {
                    return `une demi-droite qui part de ${coordonneesLues(o.a)} et passe par ${coordonneesLues(o.b)}`;
                }
                return `une droite qui passe par ${coordonneesLues(o.a)} et ${coordonneesLues(o.b)}`;
            case 'cercle':
                return `un cercle de centre ${coordonneesLues(o.centre)} et de rayon ${nombre(o.rayon)}`;
            case 'polygone': {
                return `un ${nomPolygone(o.sommets.length)} de sommets ${enListe(o.sommets.map(coordonneesLues))}`;
            }
            case 'vecteur':
                return `un vecteur qui va de ${coordonneesLues(o.de)} à ${coordonneesLues(o.a)}`;
            default:
                return null;
        }
    };
    return (o) => {
        const def = o.def;
        const r = resolu(o.id);
        const nom = o.nom !== undefined ? ` ${o.nom}` : '';
        switch (def.type) {
            case 'point_libre':
                return `le point${nom} ${coordonneesLues({ x: def.x, y: def.y })}`;
            case 'milieu': {
                const p = point(o.id);
                return `le milieu de ${cite(def.de)} et ${cite(def.a)}${p !== null ? `, en ${coordonneesLues(p)}` : ''}`;
            }
            case 'intersection': {
                const p = point(o.id);
                const en = p !== null ? `, en ${coordonneesLues(p)}` : '';
                /* L3 (F3) : deux arcs de compas qui se coupent — le geste de la médiatrice. */
                const arc = (id) => doc.objets.find((x) => x.id === id)?.def.type === 'cercle_report';
                if (arc(def.de) && arc(def.a))
                    return `le point où les deux arcs se coupent${en}`;
                return `le point d’intersection${p !== null ? ` en ${coordonneesLues(p)}` : ''}`;
            }
            case 'perpendiculaire':
                return `la perpendiculaire à ${cite(def.a)} qui passe par ${cite(def.par)}`;
            case 'parallele':
                return `la parallèle à ${cite(def.a)} qui passe par ${cite(def.par)}`;
            case 'mediatrice':
                return `la médiatrice du segment qui relie ${cite(def.de)} et ${cite(def.a)}`;
            case 'bissectrice':
                return `la bissectrice de l’angle de sommet ${cite(def.sommet)}`;
            case 'cercle_par_point':
                return `un cercle de centre ${cite(def.centre)} qui passe par ${cite(def.par)}`;
            case 'esquisse': {
                /* L6 (F8) : ce que le code LIT de l'esquisse — ce que l'élève confirme. */
                const lue = reduireEsquisse(def.traits, resolue.repere);
                return lue.lue
                    ? decrireCaracteristiques(lue.caracteristiques, resolue.repere)
                    : 'un trait qu’on n’arrive pas à lire';
            }
            case 'cercle_report': {
                /* L3 (F3) : le compas — l'ouverture prise sur un segment, puis le centre. */
                const rayon = r?.genre === 'cercle' ? `, de rayon ${nombre(r.rayon)}` : '';
                return `un arc de compas de centre ${cite(def.centre)}${rayon}`;
            }
            case 'image': {
                const quoi = forme(r);
                const par = parLaTransformation(def.par);
                const image = `l’image de ${cite(def.source)}${par !== '' ? ` ${par}` : ''}`;
                return quoi !== null ? `${image} : ${quoi}` : image;
            }
            default: {
                const quoi = def.type === 'droite' ||
                    def.type === 'segment' ||
                    def.type === 'demi_droite' ||
                    def.type === 'cercle_rayon' ||
                    def.type === 'polygone' ||
                    def.type === 'vecteur'
                    ? forme(r)
                    : null;
                if (quoi !== null)
                    return quoi;
                /* La phrase de la liste des objets, sans sa majuscule. */
                const texte = repli.get(o.id) ?? '';
                return texte.charAt(0).toLowerCase() + texte.slice(1);
            }
        }
    };
}
/**
 * Atelier de figures, L7 — le MÊME lecteur, ouvert à la console du tuteur : la
 * construction de l'élève rejouée pas à pas (`construction-rejouee.ts`) et la
 * désignation d'un objet par une note du tuteur se disent avec les mots de
 * « On a lu… », jamais avec d'autres.
 */
export function lecteurDeConstruction(doc, enonce) {
    const resolue = resoudreConstruction(doc, enonce);
    return { lire: lecteur(doc, resolue), resolue };
}
/** Le début de la relecture (SPEC § 7.7). */
export const ON_A_LU = 'On a lu :';
/**
 * Ce que l'élève a tracé, dit en mots (relecture du geste, SPEC § 7.7).
 *
 * Les objets FINAUX : ceux qu'aucun autre objet de l'élève ne cite. Les points
 * d'une droite sont dans la phrase de la droite ; un point seul, demandé pour
 * lui-même, est dit. Un objet masqué, une mesure, un texte, une
 * transformation (elle se dit dans l'image qu'elle produit) ne le sont pas.
 *
 * `enonce` : la figure d'énoncé, normalisée — ses points se citent par leur
 * nom (« la parallèle à (AB) qui passe par C »).
 */
export function decrireGesteDeTrace(doc, enonce) {
    const objets = Array.isArray(doc?.objets) ? doc.objets : [];
    const resolue = resoudreConstruction(doc, enonce);
    const cites = new Set();
    for (const o of objets)
        for (const ref of referencesDe(o.def))
            cites.add(ref.id);
    const lire = lecteur(doc, resolue);
    const tousFinaux = objets.filter((o) => !cites.has(o.id) &&
        o.masque !== true &&
        o.copieDe === undefined &&
        !HORS_REPONSE.has(o.def.type) &&
        resolue.objets.get(o.id)?.genre !== 'indefini');
    /* L3 (F3) : un arc de compas est un TRAIT DE CONSTRUCTION. Il ne se lit pas
       comme la réponse quand autre chose l'est ; seul, il est tout ce qui a été
       tracé, et il se dit. */
    const sansArcs = tousFinaux.filter((o) => o.def.type !== 'cercle_report');
    const finaux = sansArcs.length > 0 ? sansArcs : tousFinaux;
    const morceaux = finaux.map(lire).filter((t) => t !== '');
    return {
        phrase: morceaux.length === 0 ? `${ON_A_LU} rien encore.` : `${ON_A_LU} ${enListe(morceaux)}.`,
        objets: morceaux,
    };
}
/**
 * La construction de référence, étape par étape : un objet de la solution
 * par étape, dans l'ordre où le prof l'a écrite (la `solution` d'un tracé est
 * déjà ordonnée). Une transformation, une mesure, un texte ne sont pas une
 * étape à montrer : ils n'ont pas de trait.
 */
export function etapesDeLaConstruction(attendu, enonce) {
    const objets = Array.isArray(attendu?.objets) ? attendu.objets : [];
    const resolue = resoudreConstruction(attendu, enonce);
    const lire = lecteur(attendu, resolue);
    const etapes = [];
    for (const o of objets) {
        if (o.masque === true || HORS_REPONSE.has(o.def.type))
            continue;
        const quoi = lire(o);
        if (quoi === '')
            continue;
        const r = resolue.objets.get(o.id);
        const verbe = r?.genre === 'point' ? 'On place' : 'On trace';
        etapes.push({ rang: etapes.length + 1, id: o.id, phrase: `${verbe} ${quoi}.` });
    }
    return etapes;
}
/**
 * Le document de la construction jusqu'à l'étape `rang` (1 = la première) :
 * les objets montrés, et ceux qu'ils citent — une transformation reste, sans
 * trait, pour que l'image se résolve.
 */
export function constructionJusquA(attendu, etapes, rang) {
    const montres = new Set(etapes.filter((e) => e.rang <= rang).map((e) => e.id));
    const garde = new Set(montres);
    /* Les objets cités par un objet montré (les transformations, d'abord) restent, cachés. */
    const parId = new Map(attendu.objets.map((o) => [o.id, o]));
    const pile = [...montres];
    while (pile.length > 0) {
        const id = pile.pop();
        const o = parId.get(id);
        if (o === undefined)
            continue;
        for (const ref of referencesDe(o.def)) {
            if (!garde.has(ref.id) && parId.has(ref.id)) {
                garde.add(ref.id);
                pile.push(ref.id);
            }
        }
    }
    return {
        ...attendu,
        objets: attendu.objets
            .filter((o) => garde.has(o.id))
            .map((o) => (montres.has(o.id) ? o : { ...o, masque: true })),
    };
}
//# sourceMappingURL=decrire-geste.js.map